import type { EquivalentDomainGroupWire } from '../api/equivalentDomainsApi'

/**
 * T-G6: vault item URL / username → canonical domain extraction + group lookup.
 *
 * 動作:
 *   - URL string から host を取り出して lowercase 化 (`https://Login.GOOGLE.com:443/x` → `login.google.com`)
 *   - 失敗時 (URL parse 不能 / 空文字) は null
 *   - 同じ group に属する domain (exact match) は同一視 → "canonical" として group 名 or
 *     代表 domain (domains[0]) を返す
 *
 * useVaultList から呼ばれて Vault 一覧の表示 grouping に使う。
 */

export const extractHost = (raw: string | null | undefined): string | null => {
  if (!raw) return null
  const trimmed = raw.trim()
  if (trimmed === '') return null

  // URL に scheme が無い場合は http:// を補って parse する
  const candidate = /^[a-z][a-z0-9+\-.]*:/iu.test(trimmed) ? trimmed : `http://${trimmed}`
  try {
    const u = new URL(candidate)

    return u.hostname.toLowerCase()
  } catch {
    return null
  }
}

/**
 * `host` (e.g. "login.google.com") が group の `domains` (e.g. ["google.com", "gmail.com"]) の
 * いずれかと「同じか subdomain」で一致するかを判定する。
 *
 * 例:
 *   host="login.google.com", domain="google.com" → true
 *   host="google.com",       domain="google.com" → true
 *   host="googlefake.com",   domain="google.com" → false (suffix match 必須)
 */
const matchesDomain = (host: string, domain: string): boolean => {
  if (host === domain) return true

  return host.endsWith(`.${domain}`)
}

/**
 * `host` が属する group を返す (該当無しなら null)。同じ host が複数 group に
 * 属するケースは設計上ありえないが、念のため最初の hit を返す。
 */
export const findGroupForHost = (
  host: string,
  groups: readonly EquivalentDomainGroupWire[],
): EquivalentDomainGroupWire | null => {
  for (const group of groups) {
    for (const domain of group.domains) {
      if (matchesDomain(host, domain)) return group
    }
  }

  return null
}

/**
 * `host` の「表示用 canonical key」を返す。group に属するなら `group:${groupId}` を返し、
 * 属さないなら host そのものを返す。useVaultList の `useMemo` で `Map<canonical, items[]>`
 * を作る用途。
 */
export const canonicalKeyForHost = (
  host: string | null,
  groups: readonly EquivalentDomainGroupWire[],
): string => {
  if (host === null) return '(no-host)'
  const group = findGroupForHost(host, groups)
  if (group) return `group:${group.id}`

  return host
}
